import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { track, newEventId } from "../lib/track";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Monotonic token that identifies the most recent auth action.
    // Any in-flight refresh() with a stale token is ignored so a late
    // /auth/me 401 cannot clobber a fresh login/register state.
    const authSeq = useRef(0);

    const refresh = useCallback(async () => {
        const seq = ++authSeq.current;
        try {
            const { data } = await api.get("/auth/me");
            if (seq !== authSeq.current) return null; // stale response, ignore
            setUser(data);
            return data;
        } catch {
            if (seq !== authSeq.current) return null; // stale response, ignore
            setUser(null);
            return null;
        } finally {
            // Only the newest in-flight refresh may end the loading state.
            // (Under StrictMode the mount effect runs twice; without this the
            // first, ignored response would flip loading=false while user is
            // still null and bounce protected routes to /login on hard reload.)
            if (seq === authSeq.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If returning from Google OAuth, let AuthCallback establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        authSeq.current++; // invalidate any in-flight refresh()
        setUser(data.user);
        return data.user;
    };
    const register = async (name, email, password) => {
        const eid = newEventId();
        const { data } = await api.post("/auth/register", { name, email, password, event_id: eid });
        authSeq.current++; // invalidate any in-flight refresh()
        setUser(data.user);
        try { track("CompleteRegistration", { content_name: "Account signup", status: true }, { eventID: eid }); } catch { /* noop */ }
        return data.user;
    };
    const loginWithGoogle = async (credential) => {
        const eid = newEventId();
        const { data } = await api.post("/auth/google", { credential, event_id: eid });
        authSeq.current++; // invalidate any in-flight refresh()
        setUser(data.user);
        if (data.created) {
            try { track("CompleteRegistration", { content_name: "Account signup (Google)", status: true }, { eventID: eid }); } catch { /* noop */ }
        }
        return data.user;
    };
    const logout = async () => {
        await api.post("/auth/logout");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refresh, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
